"use strict";
/**
 * 标准段名清单：模板与页面设置之间的**接口**。
 *
 * 一句话分工：**模板画格子，页面设置填格子。** 格子靠 `instance_id` 认领内容 ——
 * 客户在极光下写好的「一段公司简介」，存在 `page_blocks` 里，标着
 * `instance_id = 'home-about'`；换到另一套主题时，那套主题的版式里只要也有一个
 * 叫 `home-about` 的格子，同一段文字就原地出现在新位置上。
 *
 * 所以 `instance_id` 不是内部编号，它是**跨主题的内容地址**。没有它，换主题
 * 就等于把客户写过的所有字丢掉（数据还在库里，只是没有一个格子去接，界面上
 * 表现为"整站的字全没了"，且看不出原因）。
 *
 * 三件由此推出的事：
 *
 *   1) **标准段名必须是一份公开清单。** 内置四套各写各的，靠人记；上架校验
 *      一拦，才知道哪套的 `home-feature` 被写成了 `feature-1`。
 *   2) **清单只管"名字 ↔ 语义"，不管"用什么组件"。** 同一个 `home-feature`
 *      在极光是图文分栏、在别的主题里可以是卡片组 —— 组件是设计选择，
 *      段名是内容地址，两者不能绑死，否则主题就不敢换组件了。
 *   3) **清单外的段名允许存在，但换主题时会消失。** 这是设计自由度的代价，
 *      不该用硬校验堵死（那会让每套主题都长一个样），但必须在上架时说出来。
 *
 * 反过来，`theme-kit` 不依赖 `@icetus/blocks`（依赖方向是 blocks → contracts），
 * 所以这里不写"这个段位该用哪个组件"——那是组件注册表的真源。需要这份对应
 * 关系的地方（主题工坊插入实例时挑名字）在调用方自己拼一张建议表。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.THEME_SLOTS = void 0;
exports.slotOf = slotOf;
exports.canonicalSlotIds = canonicalSlotIds;
exports.requiredSlots = requiredSlots;
exports.validateLayoutSlots = validateLayoutSlots;
const layout_1 = require("./layout");
/**
 * 清单本体。
 *
 * 取名规则：`<页面>-<语义>`，外壳一律 `shared-` 前缀。语义按"客户会往里写什么"
 * 分，不按组件分 —— 所以是 `home-proof`（案例/证据），不是 `home-case-grid`。
 */
exports.THEME_SLOTS = [
    // ── 全站外壳 ────────────────────────────────────────────────────
    {
        id: 'shared-utility',
        page: 'shared',
        region: 'utility',
        label: { zh: '顶部工具条', en: 'Utility bar' },
        note: '页头上面那条细栏：电话、邮箱、语言切换。',
    },
    {
        id: 'shared-logo',
        page: 'shared',
        region: 'header',
        label: { zh: '站点标识', en: 'Site identity' },
        note: 'Logo 与站点名。换主题时最不该丢的东西。',
    },
    {
        id: 'shared-nav',
        page: 'shared',
        region: 'header',
        label: { zh: '主导航', en: 'Primary nav' },
        note: '按站点已发布的页面自动生成，主题只声明它摆在哪里。',
        required: true,
    },
    {
        id: 'shared-cta',
        page: 'shared',
        region: 'header',
        label: { zh: '页头按钮', en: 'Header CTA' },
        note: '页头右侧的「联系我们」「获取报价」一类按钮。',
    },
    {
        id: 'shared-footer',
        page: 'shared',
        region: 'footer',
        label: { zh: '页脚', en: 'Footer' },
        note: '公司信息、联系方式、站内链接。',
    },
    // ── 首页 ────────────────────────────────────────────────────────
    {
        id: 'home-hero',
        page: 'home',
        region: 'hero',
        label: { zh: '首屏', en: 'Hero' },
        note: '四种构图（分栏 / 全幅 / 居中大字 / 轮播）任选一种，整个首屏区域只有它。',
        required: true,
    },
    {
        id: 'home-intro-heading',
        page: 'home',
        region: 'main',
        label: { zh: '能力区标题', en: 'Capabilities heading' },
        note: '下面那组能力卡的标题。',
    },
    {
        id: 'home-intro',
        page: 'home',
        region: 'main',
        label: { zh: '能力 / 卖点', en: 'Capabilities' },
        note: '通常是一组卡片，讲「我们能做什么」。',
    },
    {
        id: 'home-feature',
        page: 'home',
        region: 'main',
        label: { zh: '图文分栏', en: 'Feature split' },
        note: '一张图配一段说明，讲一项具体能力或工艺。',
    },
    {
        id: 'home-about',
        page: 'home',
        region: 'main',
        label: { zh: '公司简介', en: 'About' },
        note: '一段正文，讲公司本身：年限、产能、认证。',
    },
    {
        id: 'home-proof',
        page: 'home',
        region: 'main',
        label: { zh: '案例 / 证据', en: 'Case studies' },
        note: '做过什么、给谁做过。内容来自案例库。',
    },
    {
        id: 'home-partners',
        page: 'home',
        region: 'main',
        label: { zh: '合作伙伴', en: 'Partners' },
        note: '经销商、认证机构、合作品牌。',
    },
    {
        id: 'home-clients',
        page: 'home',
        region: 'main',
        label: { zh: '客户 Logo 墙', en: 'Client logos' },
        note: '一排客户标识，主要作用是信任。',
    },
    {
        id: 'home-process',
        page: 'home',
        region: 'main',
        label: { zh: '流程', en: 'Process' },
        note: '从询盘到交付分几步，外贸站最常见的说服段。',
    },
    {
        id: 'home-stats',
        page: 'home',
        region: 'main',
        label: { zh: '数据条', en: 'Stats' },
        note: '一排关键数字：年限、国家数、库存、复购率。与「流程」是两种语义，别互相顶替。',
    },
    {
        id: 'home-news',
        page: 'home',
        region: 'main',
        label: { zh: '新闻 / 文章', en: 'News' },
        note: '首页露出的三到六条动态，通常连同「查看全部」一起收口。',
    },
    {
        id: 'home-cta',
        page: 'home',
        region: 'main',
        label: { zh: '转化区', en: 'Call to action' },
        note: '页面中后段的行动号召，通常也出现在页尾。',
    },
    {
        id: 'home-products-heading',
        page: 'home',
        region: 'main',
        label: { zh: '商品区标题', en: 'Products heading' },
        note: '商品网格上面那行标题。',
    },
    {
        id: 'home-products',
        page: 'home',
        region: 'main',
        label: { zh: '商品网格', en: 'Product grid' },
        note: '首页的商品陈列，条目来自商品库而不是手填。',
    },
    // ── 商品列表页 ──────────────────────────────────────────────────
    {
        id: 'products-heading',
        page: 'products',
        region: 'main',
        label: { zh: '页标题', en: 'Page heading' },
        note: '「Products」这一行，客户可改文案。',
    },
    {
        id: 'products-cats',
        page: 'products',
        region: 'main',
        label: { zh: '分类筛选', en: 'Category filter' },
        note: '分类卡或筛选条，内容来自分类库。',
    },
    {
        id: 'products-grid',
        page: 'products',
        region: 'main',
        label: { zh: '商品网格', en: 'Product grid' },
        note: '这一页的主体。少了它，客户的商品在列表页无处显示。',
        required: true,
    },
    // ── 商品详情页 ──────────────────────────────────────────────────
    {
        id: 'product-body',
        page: 'product',
        region: 'main',
        label: { zh: '商品正文', en: 'Product body' },
        note: '图集、参数、描述、询盘按钮。主题只决定它周围排什么。',
        required: true,
    },
    // ── 联系页 ──────────────────────────────────────────────────────
    {
        id: 'contact-heading',
        page: 'contact',
        region: 'main',
        label: { zh: '页标题', en: 'Page heading' },
        note: '「Contact us」这一行。',
    },
    {
        id: 'contact-form',
        page: 'contact',
        region: 'main',
        label: { zh: '询盘表单', en: 'Contact form' },
        note: '客户收询盘的唯一入口。少了它这一页没有意义。',
        required: true,
    },
    {
        id: 'contact-faq',
        page: 'contact',
        region: 'main',
        label: { zh: '常见问题', en: 'FAQ' },
        note: '可选。放在表单旁边挡掉重复提问。',
    },
    // ── 普通内容页 ──────────────────────────────────────────────────
    {
        id: 'page-body',
        page: 'page',
        region: 'main',
        label: { zh: '正文', en: 'Body' },
        note: '富文本，客户在控制台里写。商品详情与联系页没有版式时也靠它兜底。',
        required: true,
    },
];
const BY_ID = new Map(exports.THEME_SLOTS.map((slot) => [slot.id, slot]));
/** 这个 id 是不是标准段名。不是标准段名不报错 —— 只是换主题时它接不到内容 */
function slotOf(id) {
    return BY_ID.get(id);
}
/** 某页面某区域下的标准段名。控制台、工坊用它做"这里应该叫什么"的提示 */
function canonicalSlotIds(page, region) {
    return exports.THEME_SLOTS.filter((slot) => slot.page === page && slot.region === region).map((slot) => slot.id);
}
function requiredSlots() {
    return exports.THEME_SLOTS.filter((slot) => slot.required);
}
const pageName = (page) => page === 'shared' ? '全站外壳' : layout_1.PAGE_KIND_LABEL[page].zh;
const regionName = (region) => layout_1.REGION_LABEL[region].zh;
/**
 * 段名校验。
 *
 * 分三档，取舍写在每档的注释里：
 *   - **必备段缺失** → 错误（客户内容没有落点）
 *   - **标准段放错页面/区域** → 错误（名字对、位置不对，等于没接上）
 *   - **清单外的段名** → 警告（设计自由，但要说清代价）
 *
 * 页面整块没声明时，必备段降为警告：那意味着这套主题走了降级链或老路径，
 * 是"这套主题不做这一页"，不是"这套主题漏了一段"。
 */
function validateLayoutSlots(layout) {
    const errors = [];
    const warnings = [];
    if (layout === null || typeof layout !== 'object')
        return { errors, warnings };
    const root = layout;
    const pages = root.pages;
    if (pages === null || typeof pages !== 'object')
        return { errors, warnings };
    /** 实例出现在哪。形状问题（非数组、未知区域）交给 validateLayoutShape，这里只收能认的 */
    const placed = new Map();
    const declared = new Set();
    const collect = (page, pageLayout) => {
        if (pageLayout === null || typeof pageLayout !== 'object')
            return;
        const entries = Object.entries(pageLayout);
        if (entries.length === 0)
            return;
        declared.add(page);
        for (const [regionKey, items] of entries) {
            if (!Array.isArray(items))
                continue;
            const region = regionKey;
            for (const item of items) {
                if (!item || typeof item !== 'object')
                    continue;
                const id = item.id;
                if (typeof id === 'string' && id)
                    placed.set(id, { page, region });
            }
        }
    };
    collect('shared', root.shared);
    for (const [pageKey, pageLayout] of Object.entries(pages)) {
        collect(pageKey, pageLayout);
    }
    // ── 必备段 ──────────────────────────────────────────────────────
    for (const slot of requiredSlots()) {
        const where = placed.get(slot.id);
        if (!where) {
            if (declared.has(slot.page)) {
                errors.push({
                    page: slot.page,
                    region: slot.region,
                    itemId: slot.id,
                    message: `缺少必备段位「${slot.id}」（${pageName(slot.page)} · ${regionName(slot.region)} ：${slot.label.zh}）—— ${slot.note}`,
                });
            }
            continue;
        }
        if (where.page !== slot.page || where.region !== slot.region) {
            errors.push({
                page: slot.page,
                region: slot.region,
                itemId: slot.id,
                message: `「${slot.id}」是 ${pageName(slot.page)} · ${regionName(slot.region)} 的段位，却被放在 ${pageName(where.page)} · ${regionName(where.region)}`,
            });
        }
    }
    // 非必备的标准段也要判位置：名字对、位置错，客户的内容一样接不上
    for (const [id, where] of placed) {
        const slot = slotOf(id);
        if (!slot)
            continue;
        if (slot.required)
            continue; // 上面判过
        if (where.page !== slot.page || where.region !== slot.region) {
            errors.push({
                page: slot.page,
                region: slot.region,
                itemId: id,
                message: `「${id}」是 ${pageName(slot.page)} · ${regionName(slot.region)} 的段位，却被放在 ${pageName(where.page)} · ${regionName(where.region)}`,
            });
        }
    }
    // ── 页面整块没声明 ──────────────────────────────────────────────
    /**
     * 只提示"首页"和"外壳"。
     *
     * 其余三种页面有降级链（商品/详情/联系 → 内容页），没写是正常的；
     * 首页与外壳不退，缺了就是另一套风格的站：首页掉回老路径（硬编码页头 +
     * 客户自己堆的区块），外壳缺失则每个页面各自长一个样。
     */
    for (const page of ['shared', 'home']) {
        if (declared.has(page))
            continue;
        warnings.push({
            page,
            message: page === 'shared'
                ? '这套主题没有声明全站外壳（页头 / 页脚），客户站点会掉回老路径的硬编码页头'
                : '这套主题没有声明首页版式，客户首页会掉回老路径，与其它页面不是一套风格',
        });
    }
    // ── 清单外的段名 ────────────────────────────────────────────────
    const custom = [...placed.keys()].filter((id) => !slotOf(id));
    if (custom.length > 0) {
        const head = custom.slice(0, 8).join('、');
        warnings.push({
            message: `有 ${custom.length} 个自定义段名：${head}${custom.length > 8 ? ' 等' : ''}。` +
                '它们只在这套主题里有效 —— 客户换到别的主题时，这些段的内容会隐藏（数据不删）。' +
                '如果这段的含义与某个标准段位相同，请改用标准段名。',
        });
    }
    return { errors, warnings };
}
//# sourceMappingURL=slots.js.map